package com.eladfinish.bible.contest.screens.main.menu

import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Share
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.ad_free_for_minutes_action
import com.eladfinish.bible.contest.ads.AdFreeReward
import com.eladfinish.bible.contest.check_update
import com.eladfinish.bible.contest.common.gender.HebrewGender
import com.eladfinish.bible.contest.common.gender.personalizeGender
import com.eladfinish.bible.contest.help
import com.eladfinish.bible.contest.ic_help
import com.eladfinish.bible.contest.platform.isAndroid
import com.eladfinish.bible.contest.platform.isDesktop
import com.eladfinish.bible.contest.platform.isWeb
import com.eladfinish.bible.contest.purchase_debug_consume_test_purchase
import com.eladfinish.bible.contest.purchase_restore
import com.eladfinish.bible.contest.rate
import com.eladfinish.bible.contest.share
import com.eladfinish.bible.contest.supporting_the_app
import com.eladfinish.bible.contest.ui.components.toolbar.SetToolbarConfiguration
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarActionConfig
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarActionPlacement
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarConfiguration
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarHeaderComponentConfig
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarIcon
import com.eladfinish.bible.contest.ui.components.toolbar.model.ToolbarKind
import com.eladfinish.bible.contest.ui.icons.AppIcons
import com.eladfinish.bible.contest.ui.menus.actions.MainToolbarAction
import com.eladfinish.bible.contest.ui.menus.actions.ToolbarActionEmitter
import com.eladfinish.bible.contest.upgrade
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.desc.desc
import dev.icerock.moko.resources.format
import kotlinx.collections.immutable.toImmutableList

enum class MainToolbarId {
    RATE,
    SHARE,
    CHECK_UPDATE,
    UPGRADE,
    HELP,
    SHOW_AD,
    AD_FREE_HOUR,
    RESTORE_PURCHASE,
    CONSUME_TEST_PURCHASE,
}

@Composable
internal fun MainMenu(
    title: String,
    toolbarEmitter: ToolbarActionEmitter<MainToolbarAction>,
    hebrewGender: HebrewGender,
    shouldShowAds: Boolean,
    canPurchaseAdFree: Boolean,
    isDebugMode: Boolean,
    isHelpInToolbar: Boolean,
) {
    SetToolbarConfiguration(
        configuration = ToolbarConfiguration(
            kind = ToolbarKind.Main,
            title = ToolbarHeaderComponentConfig.Text(text = title),
            actions = listOfNotNull(
                ToolbarActionConfig.IconAction(
                    id = MainToolbarId.AD_FREE_HOUR,
                    icon = ToolbarIcon.VectorIcon(AppIcons.AdsClick),
                    menuText = SharedRes.strings.ad_free_for_minutes_action.format(AdFreeReward.MINUTES),
                    onClick = { toolbarEmitter.emit(action = MainToolbarAction.AdFreeHour) },
                ).takeIf { shouldShowAds },
                ToolbarActionConfig.IconAction(
                    id = MainToolbarId.SHOW_AD,
                    icon = ToolbarIcon.VectorIcon(AppIcons.AdsClick),
                    menuText = SharedRes.strings.supporting_the_app.desc(),
                    onClick = { toolbarEmitter.emit(action = MainToolbarAction.ShowAd) },
                ).takeIf { false },
                ToolbarActionConfig.IconAction(
                    id = MainToolbarId.RATE,
                    icon = ToolbarIcon.VectorIcon(AppIcons.StarRate),
                    menuText = SharedRes.strings.rate.desc().personalizeGender(hebrewGender = hebrewGender),
                    onClick = { toolbarEmitter.emit(action = MainToolbarAction.Rate) },
                ).takeIf { !isWeb }, // a website has no store listing to rate
                ToolbarActionConfig.IconAction(
                    id = MainToolbarId.SHARE,
                    icon = ToolbarIcon.VectorIcon(Icons.Default.Share),
                    menuText = SharedRes.strings.share.desc().personalizeGender(hebrewGender = hebrewGender),
                    onClick = { toolbarEmitter.emit(action = MainToolbarAction.Share) },
                    placement = if (shouldShowAds) ToolbarActionPlacement.InOverflow else ToolbarActionPlacement.OnToolbar,
                ),
                ToolbarActionConfig.IconAction(
                    id = MainToolbarId.HELP,
                    icon = ToolbarIcon.PainterIcon(
                        painterResource(SharedRes.images.ic_help)
                    ),
                    menuText = SharedRes.strings.help.desc(),
                    onClick = { toolbarEmitter.emit(action = MainToolbarAction.Help) },
                    placement = ToolbarActionPlacement.InOverflow,
                ).takeIf { isHelpInToolbar },
                ToolbarActionConfig.IconAction(
                    id = MainToolbarId.CHECK_UPDATE,
                    icon = ToolbarIcon.VectorIcon(AppIcons.Update),
                    menuText = SharedRes.strings.check_update.desc(),
                    onClick = { toolbarEmitter.emit(action = MainToolbarAction.CheckUpdate) },
                    placement = ToolbarActionPlacement.InOverflow,
                    iconColor = Color.Black,
                ).takeIf { isAndroid || isDesktop },
                ToolbarActionConfig.IconAction(
                    id = MainToolbarId.UPGRADE,
                    icon = ToolbarIcon.VectorIcon(AppIcons.Upgrade),
                    menuText = SharedRes.strings.upgrade.desc(),
                    onClick = { toolbarEmitter.emit(action = MainToolbarAction.Upgrade) },
                    placement = ToolbarActionPlacement.InOverflow,
                    iconColor = Color.Black,
                ).takeIf { false },
                ToolbarActionConfig.IconAction(
                    id = MainToolbarId.RESTORE_PURCHASE,
                    icon = ToolbarIcon.VectorIcon(AppIcons.Restore),
                    menuText = SharedRes.strings.purchase_restore.desc(),
                    onClick = { toolbarEmitter.emit(action = MainToolbarAction.RestorePurchase) },
                    placement = ToolbarActionPlacement.InOverflow,
                    iconColor = Color.Black,
                ).takeIf { canPurchaseAdFree },
                ToolbarActionConfig.IconAction(
                    id = MainToolbarId.CONSUME_TEST_PURCHASE,
                    icon = ToolbarIcon.VectorIcon(AppIcons.BugReport),
                    menuText = SharedRes.strings.purchase_debug_consume_test_purchase.desc(),
                    onClick = { toolbarEmitter.emit(action = MainToolbarAction.ConsumeTestPurchase) },
                    placement = ToolbarActionPlacement.InOverflow,
                    iconColor = Color(0xFFFF6B00),
                ).takeIf { isAndroid && isDebugMode },
            ).toImmutableList(),
        )
    )
}
